import { useQuery } from '@apollo/client'
import { Card } from '@faceup/ui'
import { Button } from '@faceup/ui-base'
import { ChannelType } from '@faceup/utils'
import { useState } from 'react'
import { graphql } from '../__generated__'
import { CreateAiHotlineModal } from './CreateAiHotlineModal'
import { CreateAutomatedHotlineModal } from './CreateAutomatedHotlineModal'
import { CreateLiveHotlineModal } from './CreateLiveHotlineModal'
import { ReportSourcesHotlineTable } from './ReportSourcesHotlineTable'
import { ReportSourcesTable } from './ReportSourcesTable'

const query = {
  ReportSourcesQuery: graphql(`
    query ReportSourcesQuery($motherId: UUID!) {
      reportSources(
        motherId: $motherId
        page: 0
        rowsPerPage: 100
        types: [ReportingSystem, AutomatedHotline, AiHotline, LiveHotline]
      ) {
        items {
          id
          sourceType
          ...ReportSourcesTable_reportSource
          ...ReportSourceHotlineTable_hotline
          ...TransferHotlineNumberModal_hotline
        }
      }
    }
  `),
}

type ReportSourcesCardProps = {
  institutionId: string
}

export const ReportSourcesCard = ({ institutionId }: ReportSourcesCardProps) => {
  const [openCreateAutomatedHotlineModal, setOpenCreateAutomatedHotlineModal] = useState(false)
  const [openCreateLiveHotlineModal, setOpenCreateLiveHotlineModal] = useState(false)
  const [openCreateAiHotlineModal, setOpenCreateAiHotlineModal] = useState(false)
  const { data, loading, refetch } = useQuery(query.ReportSourcesQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      motherId: institutionId,
    },
  })

  const reportSources = data?.reportSources?.items ?? []

  const groupedReportSources = reportSources.reduce(
    (acc, item) => ({
      ...acc,
      [item.sourceType]: [...acc[item.sourceType], item],
    }),
    {
      [ChannelType.AutomatedHotline]: [],
      [ChannelType.LiveHotline]: [],
      [ChannelType.AiHotline]: [],
      [ChannelType.ReportingSystem]: [],
      // surveys are filtered in GQL query, left here just for the types
      [ChannelType.Survey]: [],
    }
  )

  const transferableHotlines = [
    ...groupedReportSources[ChannelType.AutomatedHotline],
    ...groupedReportSources[ChannelType.AiHotline],
  ]

  return (
    <Card>
      <ReportSourcesTable
        reportSources={groupedReportSources[ChannelType.ReportingSystem]}
        loading={loading}
      />
      <Card.Section>
        <Card.Header
          extra={
            <Button type='primary' onClick={() => setOpenCreateAutomatedHotlineModal(true)}>
              Order automated hotline
            </Button>
          }
        >
          Automated Hotline
        </Card.Header>
        <Card.Content>
          <ReportSourcesHotlineTable
            reportSources={groupedReportSources[ChannelType.AutomatedHotline]}
            transferableHotlines={transferableHotlines}
            loading={loading}
            refetch={refetch}
            title='Automated Hotlines'
          />
        </Card.Content>
        <CreateAutomatedHotlineModal
          open={openCreateAutomatedHotlineModal}
          onClose={() => setOpenCreateAutomatedHotlineModal(false)}
          institutionId={institutionId}
        />
      </Card.Section>
      <Card.Section>
        <Card.Header
          extra={
            <Button type='primary' onClick={() => setOpenCreateAiHotlineModal(true)}>
              Order AI hotline
            </Button>
          }
        >
          AI Hotline
        </Card.Header>
        <Card.Content>
          <ReportSourcesHotlineTable
            reportSources={groupedReportSources[ChannelType.AiHotline]}
            transferableHotlines={transferableHotlines}
            loading={loading}
            refetch={refetch}
            title='AI Hotlines'
          />
        </Card.Content>
        <CreateAiHotlineModal
          open={openCreateAiHotlineModal}
          onClose={() => setOpenCreateAiHotlineModal(false)}
          institutionId={institutionId}
        />
      </Card.Section>
      <Card.Section>
        <Card.Header
          extra={
            <Button type='primary' onClick={() => setOpenCreateLiveHotlineModal(true)}>
              Request live hotline
            </Button>
          }
        >
          Live Hotline
        </Card.Header>
        <Card.Content>
          <ReportSourcesHotlineTable
            reportSources={groupedReportSources[ChannelType.LiveHotline]}
            loading={loading}
            refetch={refetch}
            title='Live Hotlines'
          />
        </Card.Content>
        <CreateLiveHotlineModal
          open={openCreateLiveHotlineModal}
          onClose={() => setOpenCreateLiveHotlineModal(false)}
          institutionId={institutionId}
        />
      </Card.Section>
    </Card>
  )
}
